import { useEffect, useState, type ChangeEvent, type FC } from "react";
import { Button, LoadingIndicator, Modal, Text } from "@uhg-harmony/react";
import type { ModalProps } from "@uhg-harmony/react";
import styled from "styled-components";
import { getSupportUploadUsernameFromToken } from "./supportToken";
import {
  getSupportImageMetadata,
  uploadSupportImage,
} from "./supportUploadApi";

type SupportModalMode = "upload" | "metadata";

type SupportUploadModalProps = ModalProps & {
  open: boolean;
  onClose: () => void;
  token?: string;
  mode?: SupportModalMode;
  onConfirm?: (file: File, userName: string) => void;
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: min(100%, 620px);
`;

const FileDropzone = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border: 1px dashed rgba(12, 85, 184, 0.35);
  border-radius: 16px;
  background: rgba(12, 85, 184, 0.04);
  cursor: pointer;
`;

const FileInput = styled.input`
  width: 100%;
`;

const PreviewFrame = styled.div`
  min-height: 260px;
  display: grid;
  place-items: center;
  border-radius: 16px;
  border: 1px solid rgba(16, 33, 58, 0.1);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98),
    rgba(239, 245, 255, 0.86)
  );
  overflow: hidden;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  max-height: 360px;
  object-fit: contain;
`;

const TextInput = styled.input`
  width: 100%;
  border: 1px solid rgba(16, 33, 58, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const FieldLabel = styled.label`
  margin: 0;
`;

const FieldLabelText = styled(Text)`
  margin: 0;
`;

const ApiResponseBlock = styled.pre`
  margin: 0;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(16, 33, 58, 0.15);
  background: rgba(16, 33, 58, 0.04);
  color: #10213a;
  font-size: 12px;
  line-height: 1.4;
  max-height: 180px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

function getUploadFileName(fileName: string): string {
  return fileName.toLowerCase().endsWith(".webp")
    ? fileName.replace(/\.webp$/i, ".png")
    : fileName;
}

export const SupportUploadModal: FC<SupportUploadModalProps> = ({
  title,
  open,
  onClose,
  token,
  mode = "upload",
  onConfirm,
}) => {
  const isMetadataMode = mode === "metadata";
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [platform, setPlatform] = useState("");
  const [client, setClient] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [apiResponseText, setApiResponseText] = useState("");
  const [isApiResponseModalOpen, setIsApiResponseModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const supportUserName = getSupportUploadUsernameFromToken(token);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setPreviewUrl("");
      setPlatform("");
      setClient("");
      setStatusMessage("");
      setErrorMessage("");
      setIsSubmitting(false);
    }
  }, [open]);

  const handleCloseApiResponseModal = () => {
    setIsApiResponseModalOpen(false);
    setApiResponseText("");
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const previewAltText = selectedFile?.name || "Selected image preview";

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl("");
      setStatusMessage("");
      setApiResponseText("");
      setErrorMessage("");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setStatusMessage(`Ready to submit ${getUploadFileName(file.name)}.`);
    setApiResponseText("");
    setErrorMessage("");
  };

  const handleSubmit = () => {
    if (isSubmitting) {
      return;
    }

    const trimmedPlatform = platform.trim();
    const trimmedClient = client.trim();

    if (trimmedClient && !trimmedPlatform) {
      setErrorMessage("Platform is required when Client has a value.");
      setStatusMessage("");
      setApiResponseText("");
      return;
    }

    if (isMetadataMode) {
      setErrorMessage("");
      setStatusMessage("Retrieving image metadata...");
      setApiResponseText("");
      setIsSubmitting(true);

      void getSupportImageMetadata({
        token,
        platform: trimmedPlatform,
        client: trimmedClient,
      })
        .then((response) => {
          const responseMessage =
            response.message || "Image metadata retrieved successfully.";
          setApiResponseText(
            [responseMessage, "", JSON.stringify(response, null, 2)].join("\n"),
          );
          onClose();
          setIsApiResponseModalOpen(true);
        })
        .catch((error: unknown) => {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to retrieve image metadata.",
          );
          setStatusMessage("");
          setApiResponseText("");
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      return;
    }

    if (!selectedFile) {
      setErrorMessage("Choose an image before submitting.");
      setStatusMessage("");
      setApiResponseText("");
      return;
    }

    setErrorMessage("");
    setStatusMessage("Uploading image...");
    setApiResponseText("");
    setIsSubmitting(true);

    const uploadFileName = getUploadFileName(selectedFile.name);

    void uploadSupportImage({
      token,
      file: selectedFile,
      userName: supportUserName,
      platform: trimmedPlatform,
      client: trimmedClient,
    })
      .then((response) => {
        onConfirm?.(selectedFile, supportUserName);
        const responseMessage =
          response.message ||
          (supportUserName
            ? `Submitted ${uploadFileName} as ${supportUserName}.`
            : `Submitted ${uploadFileName}.`);
        setApiResponseText(
          [responseMessage, "", JSON.stringify(response, null, 2)].join("\n"),
        );
        onClose();
        setIsApiResponseModalOpen(true);
      })
      .catch((error: unknown) => {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to upload the image.",
        );
        setStatusMessage("");
        setApiResponseText("");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      {open && (
        <Modal
          open={open}
          title={
            title || (isMetadataMode ? "getImageMetaData" : "Upload Image")
          }
          aria-label={
            typeof title === "string"
              ? title
              : isMetadataMode
                ? "getImageMetaData"
                : "Upload Image"
          }
          onClose={onClose}
        >
          <Modal.Body>
            <ModalBody>
              <Text size="l" style={{ color: "#b00020" }}>
                If Client is provided, Platform is required.
              </Text>

              {errorMessage ? (
                <Text size="large" style={{ color: "#b00020" }}>
                  {errorMessage}
                </Text>
              ) : null}

              <FieldGroup>
                <FieldLabel htmlFor="support-upload-platform">
                  <FieldLabelText size="base">
                    Platform (optional)
                  </FieldLabelText>
                </FieldLabel>
                <TextInput
                  id="support-upload-platform"
                  type="text"
                  value={platform}
                  onChange={(event) => setPlatform(event.target.value)}
                  placeholder="e.g. ECP"
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="support-upload-client">
                  <FieldLabelText size="base">Client (optional)</FieldLabelText>
                </FieldLabel>
                <TextInput
                  id="support-upload-client"
                  type="text"
                  value={client}
                  onChange={(event) => setClient(event.target.value)}
                  placeholder="e.g. ECP_Client_001"
                />
              </FieldGroup>

              {!isMetadataMode ? (
                <>
                  <FileDropzone htmlFor="support-upload-input">
                    <Text size="small">
                      PNG, JPG, JPEG, GIF, and WEBP are supported. WEBP file wll
                      be store as PNG on the server. Max file size is 10MB.
                    </Text>
                    <FileInput
                      id="support-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </FileDropzone>

                  <PreviewFrame>
                    {previewUrl ? (
                      <PreviewImage src={previewUrl} alt={previewAltText} />
                    ) : (
                      <Text size="base">No preview yet.</Text>
                    )}
                  </PreviewFrame>
                </>
              ) : null}

              {statusMessage ? <Text size="small">{statusMessage}</Text> : null}

              {isSubmitting ? (
                <LoadingIndicator
                  loading
                  loadingText={
                    isMetadataMode
                      ? "Fetching metadata..."
                      : "Uploading image..."
                  }
                  accessibilityText={
                    isMetadataMode
                      ? "Fetching image metadata"
                      : "Uploading support image"
                  }
                />
              ) : null}
            </ModalBody>
          </Modal.Body>

          <Modal.Actions>
            <Button
              variant="secondary"
              onPress={onClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleSubmit}
              isDisabled={isSubmitting}
            >
              {isMetadataMode ? "Get Metadata" : "Submit"}
            </Button>
          </Modal.Actions>
        </Modal>
      )}

      <Modal
        open={isApiResponseModalOpen}
        title={isMetadataMode ? "Metadata Response" : "Upload Response"}
        aria-label={isMetadataMode ? "Metadata Response" : "Upload Response"}
        onClose={handleCloseApiResponseModal}
      >
        <Modal.Body>
          <ModalBody>
            {apiResponseText ? (
              <ApiResponseBlock aria-label="Support upload API response">
                {apiResponseText}
              </ApiResponseBlock>
            ) : (
              <Text size="base">No response was returned.</Text>
            )}
          </ModalBody>
        </Modal.Body>
        <Modal.Actions>
          <Button variant="primary" onPress={handleCloseApiResponseModal}>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};
